import {getTestProps} from '@benchling/js-test-utils';
import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useState, useMemo, useEffect} from 'react';

import {trackEvent} from 'analytics/Analytics';
import {assert} from 'Asserts';
import {LLM_CHAT_ANALYTICS_ACTIONS} from 'constants/analytics-products/LLMChatConstants';
import {ChatType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ChatInput, useChatInputControls} from 'llm/chat-library/ChatInput';
import {ChatMessageThread} from 'llm/chat-library/ChatMessageThread';
import ChatScrollContainer from 'llm/chat-library/ChatScrollContainer';
import {getLatestApplicationState} from 'llm/chat-library/ChatStateUtil';
import {LatestUserMessageInfo} from 'llm/ChatScrollTypes';
import {useDataCaptureMessageThread} from 'llm/data-capture/useDataCaptureMessageThread';
import * as SidepanelActions from 'nav/sidepanel/SidepanelActions';
import DocumentActions from 'notebook/documents/DocumentActions';
import {waitForEntryOrTemplateSaved} from 'notebook/entries/BaseEntryUtil';
import RealtimeUpdateSubscriptionWrapper from 'realtime/RealtimeUpdateSubscriptionWrapper';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {ButtonStyle} from 'ui-library/buttons/Button';
import useIsPhoneWidth from 'ui-library/hooks/useIsPhoneWidth';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import * as modals from 'util/Modals';

import {DataCaptureDraftDocument} from './DataCaptureDraftDocument';
import {DataCaptureSideBySideLayout} from './DataCaptureSideBySideLayout';
import {DataCaptureAgentState} from './DataCaptureTypes';
import {DraftIndicatorButton} from './DraftIndicatorButton';

export interface DataCaptureChatProps {
  chatSessionId: string;
  isExpanded: boolean;
  locked?: boolean;
}

/**
 * Top-level component for the Compose chat-based experience, generally referred to as
 * "data capture" in the code.
 *
 * This is similar in structure to the deep research UI, but for now there is duplication due to
 * various differences. Some components can likely be consolidated/shared in the future.
 */
export default function DataCaptureChat({
  chatSessionId,
  isExpanded,
  locked = false,
}: DataCaptureChatProps): JSX.Element {
  const {getFileUploads, ...controls} = useChatInputControls(ChatType.DATA_CAPTURE);
  const {inputText, setInputText, selectedFiles, setSelectedFiles} = controls;
  const [hasTrackedChatOpen, setHasTrackedChatOpen] = useState<boolean>(false);
  const {events, isLoading, errorMessage, isAssistantRunning, isLastMessageFromUser, sendMessage, refetch} =
    useDataCaptureMessageThread({chatSessionId});
  const [latestUserMessageInfo, setLatestUserMessageInfo] = useState<LatestUserMessageInfo | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  // Memoize derived state to prevent unnecessary recalculations
  const latestState = useMemo(
    () => getLatestApplicationState(events) as DataCaptureAgentState | null,
    [events]
  );
  const draftDocument = useMemo(() => latestState?.draft_document, [latestState]);

  // On phone-sized screens, we show the chat by default and have an option to open the document.
  const isPhoneWidth = useIsPhoneWidth();
  const [isPhoneDocumentOpen, setIsPhoneDocumentOpen] = useState<boolean>(false);

  // Track opening of AI chat (only once per session)
  useEffect(() => {
    if (!isLoading && !hasTrackedChatOpen) {
      // TODO(BNCH-133493): openedNewChat is always false - also this should be invoked for all chat types.
      trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.OPENED_LLM_CHAT, {
        chatType: ChatType.DATA_CAPTURE,
        messageCount: events.filter((event) => event.__typename === 'DisplayMessageChatEventNode').length,
        openedNewChat: Boolean(!chatSessionId),
      });
      setHasTrackedChatOpen(true);
    }
  }, [chatSessionId, isLoading, hasTrackedChatOpen, events]);

  // Open the draft document when it becomes available
  useEffect(() => {
    if (draftDocument?.document_api_identifier && (isExpanded || isPhoneDocumentOpen)) {
      DocumentActions.open(draftDocument.document_api_identifier);
    }
  }, [draftDocument?.document_api_identifier, isExpanded, isPhoneDocumentOpen]);

  const handleSubmitMessage = useCallback(async () => {
    const message = inputText;
    const fileUploads = selectedFiles.map((file) => {
      assert(file.completedUpload != null, 'Unexpected submit with files still uploading.');
      return file.completedUpload;
    });

    // If we need to retry, show a confirmation modal
    if (isLastMessageFromUser) {
      const confirmed = await modals.confirm(
        'Alert',
        'The previous message encountered an error and will be removed. Do you want to continue?',
        ButtonStyle.PRIMARY,
        {
          acceptText: 'Continue',
          rejectText: 'Cancel',
          promiseCompatible: true,
          enableRedesign: true,
        }
      );

      if (!confirmed) {
        return;
      }
    }

    setInputText('');
    setSelectedFiles([]);
    setIsSubmitting(true);

    try {
      if (draftDocument) {
        await waitForEntryOrTemplateSaved(draftDocument.document_api_identifier, 500, 10);
      }

      await sendMessage(message, fileUploads, isLastMessageFromUser);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    draftDocument,
    inputText,
    setInputText,
    selectedFiles,
    setSelectedFiles,
    sendMessage,
    isLastMessageFromUser,
  ]);

  const handleShowDocument = useCallback(() => {
    if (isPhoneWidth) {
      setIsPhoneDocumentOpen(true);
    } else {
      SidepanelActions.expand();
    }
  }, [isPhoneWidth]);

  const handleClosePhoneDocument = useCallback(() => {
    setIsPhoneDocumentOpen(false);
  }, []);

  const refetchCallback = useCallback(() => {
    refetch();
  }, [refetch]);

  // If we have an error message, we may or may not have any past chat history to show. If there is
  // no past chat history (e.g. error on initial pageload), just show an error for the whole chat.
  // Otherwise, we will render the events we have below with a staleness message.
  if (errorMessage != null && events.length === 0) {
    return (
      <div
        className={css(styles.errorBannerContainer)}
        {...getTestProps({component: 'DataCaptureChat', element: 'error-banner-container'})}
      >
        <InlineBanner bannerType={InlineBannerType.ERROR} text={errorMessage} />
      </div>
    );
  }

  const messageThreadElement =
    isLoading || (events.length === 0 && isAssistantRunning) ? (
      <CenteredHorizontalThrobber
        {...getTestProps({component: 'DataCaptureChat', element: 'loading-throbber'})}
      />
    ) : (
      <ChatMessageThread
        events={events}
        chatSessionId={chatSessionId}
        onUpdateLatestUserMessageInfo={setLatestUserMessageInfo}
        actionButton={
          events.length > 0 && draftDocument ? <DraftIndicatorButton onClick={handleShowDocument} /> : null
        }
        readonly={false}
        progressData={null}
      />
    );

  const chatContent = (
    <ChatScrollContainer
      chatMessagesElement={messageThreadElement}
      chatInputElement={
        locked ? null : (
          <ChatInput
            chatSessionId={chatSessionId}
            {...controls}
            onSubmit={handleSubmitMessage}
            inputDisabled={isAssistantRunning || isSubmitting || errorMessage != null}
            allowedChatTypes={[ChatType.DATA_CAPTURE]}
          />
        )
      }
      latestUserMessageInfo={latestUserMessageInfo}
      autoScroll={true}
    />
  );
  const isDocumentEditable = !isAssistantRunning && !locked && !isSubmitting;
  let content;
  if (isPhoneWidth) {
    // Mobile: show chat by default, or document when the user has opened it.
    if (isPhoneDocumentOpen && draftDocument != null) {
      content = (
        <DataCaptureDraftDocument
          locked={locked}
          draftDocument={draftDocument}
          isDocumentEditable={isDocumentEditable}
          onClose={handleClosePhoneDocument}
        />
      );
    } else {
      content = chatContent;
    }
  } else if (isExpanded && draftDocument != null) {
    // Desktop expanded view with document: show side-by-side chat and document.
    content = (
      <DataCaptureSideBySideLayout
        locked={locked}
        chatContent={chatContent}
        draftDocument={draftDocument}
        isDocumentEditable={isDocumentEditable}
      />
    );
  } else {
    // Desktop collapsed and non-document view: show chat only
    content = chatContent;
  }

  const contentWithBanner = (
    <div className={css(styles.contentContainer)}>
      {errorMessage != null && !isSubmitting && !isAssistantRunning && (
        <div
          className={css(styles.errorBannerContainer)}
          {...getTestProps({component: 'DataCaptureChat', element: 'error-banner-with-chat'})}
        >
          <InlineBanner
            bannerType={InlineBannerType.ERROR}
            text={`${errorMessage} Showing previously-loaded chat.`}
          />
        </div>
      )}
      {content}
    </div>
  );

  return locked ? (
    contentWithBanner
  ) : (
    <RealtimeUpdateSubscriptionWrapper
      apiId={chatSessionId}
      refetch={refetchCallback}
      messageFilter="no_filter"
    >
      {contentWithBanner}
    </RealtimeUpdateSubscriptionWrapper>
  );
}

const styles = StyleSheet.create({
  contentContainer: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    minHeight: 0,
    flex: 1,
  },
  errorBannerContainer: {
    margin: '16px 16px 0',
  },
  messageThreadContainer: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
});
